if (!customElements.get('collection-carousel')) {
  customElements.define(
    'collection-carousel',
    class CollectionCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.viewport = this.querySelector('[data-collection-carousel-viewport]');
        this.track = this.querySelector('[data-collection-carousel-track]');
        this.primaryGroup = this.querySelector('[data-collection-carousel-primary]');
        if (!this.viewport || !this.track || !this.primaryGroup || !this.primaryGroup.children.length) return;

        this.initialized = true;
        this.speed = Math.max(5, Number(this.dataset.speed) || 30);
        this.direction = this.dataset.direction === 'rtl' ? 'rtl' : 'ltr';
        this.position = 0;
        this.groupWidth = 0;
        this.viewportWidth = 0;
        this.speedTransitioning = false;
        this.dragging = false;
        this.dragMoved = false;
        this.suppressClick = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onFocusIn = (event) => {
          if (!event.target.matches('a:focus-visible')) return;
          this.classList.add('is-keyboard-focused');
          this.updatePlaybackRate();
          const link = event.target;
          const offset = link.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
          this.position = offset;
          this.normalizePosition();
          this.viewport.scrollLeft = 0;
          this.applyTransform();
        };
        this.onFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) {
            this.classList.remove('is-keyboard-focused', 'is-touch-active');
            this.updatePlaybackRate();
          }
        };
        this.onOutsidePointerDown = (event) => {
          if (!this.contains(event.target)) this.classList.remove('is-touch-active');
        };
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onClick = this.handleClick.bind(this);
        this.updatePlaybackRate = this.updatePlaybackRate.bind(this);
        this.onSpeedTransition = (event) => {
          if (event.target !== this || event.propertyName !== '--collection-carousel-speed-factor') return;
          this.speedTransitioning = event.type === 'transitionrun';
          this.updatePlaybackRate();
        };
        this.reducedMotion.addEventListener('change', this.updatePlaybackRate);
        this.addEventListener('transitionrun', this.onSpeedTransition);
        this.addEventListener('transitionend', this.onSpeedTransition);
        this.addEventListener('transitioncancel', this.onSpeedTransition);

        this.viewport.addEventListener('focusin', this.onFocusIn);
        this.viewport.addEventListener('focusout', this.onFocusOut);
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
        document.addEventListener('pointerdown', this.onOutsidePointerDown);
        this.viewport.addEventListener('click', this.onClick, true);
        document.addEventListener('visibilitychange', this.updatePlaybackRate);

        this.resizeObserver = new ResizeObserver(() => this.scheduleRebuild());
        this.resizeObserver.observe(this.viewport);
        this.rebuild();
      }

      disconnectedCallback() {
        if (!this.initialized) return;

        cancelAnimationFrame(this.animationFrame);
        cancelAnimationFrame(this.rebuildFrame);
        this.resizeObserver?.disconnect();
        this.motion?.cancel();
        this.classList.remove('is-ready');
        this.reducedMotion.removeEventListener('change', this.updatePlaybackRate);
        this.removeEventListener('transitionrun', this.onSpeedTransition);
        this.removeEventListener('transitionend', this.onSpeedTransition);
        this.removeEventListener('transitioncancel', this.onSpeedTransition);
        this.viewport.removeEventListener('focusin', this.onFocusIn);
        this.viewport.removeEventListener('focusout', this.onFocusOut);
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
        document.removeEventListener('pointerdown', this.onOutsidePointerDown);
        this.viewport.removeEventListener('click', this.onClick, true);
        document.removeEventListener('visibilitychange', this.updatePlaybackRate);
        this.initialized = false;
      }

      scheduleRebuild() {
        cancelAnimationFrame(this.rebuildFrame);
        this.rebuildFrame = requestAnimationFrame(() => this.rebuild());
      }

      rebuild() {
        const viewportWidth = this.viewport.clientWidth;
        if (!viewportWidth || viewportWidth === this.viewportWidth) return;
        this.viewportWidth = viewportWidth;
        this.readPosition();
        const progress = this.groupWidth ? this.position / this.groupWidth : 0;
        const gap = parseFloat(getComputedStyle(this.primaryGroup).columnGap) || 0;

        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        const columns = Math.max(
          1,
          Number(isDesktop ? this.dataset.columnsDesktop : this.dataset.columnsMobile) || 1
        );
        const itemWidth = Math.max(1, (viewportWidth - gap * (columns - 1)) / columns) * (isDesktop ? 1 : 0.6);
        this.style.setProperty('--collection-carousel-item-width', `${itemWidth}px`);

        this.track.querySelectorAll('[data-collection-carousel-duplicate]').forEach((group) => group.remove());
        this.primaryGroup.querySelectorAll('[data-collection-carousel-repeat]').forEach((item) => item.remove());

        const sourceItems = Array.from(this.primaryGroup.children);
        let clonePass = 0;
        while (this.primaryGroup.scrollWidth < viewportWidth + itemWidth && clonePass < 20) {
          sourceItems.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('data-collection-carousel-repeat', '');
            clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
            this.primaryGroup.appendChild(clone);
          });
          clonePass += 1;
        }

        const duplicateGroup = this.primaryGroup.cloneNode(true);
        duplicateGroup.removeAttribute('data-collection-carousel-primary');
        duplicateGroup.setAttribute('data-collection-carousel-duplicate', '');
        duplicateGroup.setAttribute('aria-hidden', 'true');
        duplicateGroup.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        this.track.appendChild(duplicateGroup);

        this.groupWidth = this.primaryGroup.getBoundingClientRect().width;
        this.style.setProperty('--collection-carousel-distance', `${this.groupWidth}px`);
        this.style.setProperty('--collection-carousel-duration', `${this.groupWidth / this.speed}s`);
        this.classList.add('is-ready');
        this.motion = this.track.getAnimations()[0];
        this.position = progress * this.groupWidth;
        this.applyTransform();
        this.updatePlaybackRate();
      }

      updatePlaybackRate() {
        cancelAnimationFrame(this.animationFrame);
        if (!this.motion) return;
        const paused = this.dragging || this.classList.contains('is-keyboard-focused') || this.reducedMotion.matches || document.hidden;
        const speedFactor = Number(getComputedStyle(this).getPropertyValue('--collection-carousel-speed-factor'));
        this.motion.updatePlaybackRate(paused ? 0 : speedFactor);
        // Only sample CSS while speed is easing. Steady motion runs on the compositor.
        if (this.speedTransitioning) this.animationFrame = requestAnimationFrame(this.updatePlaybackRate);
      }

      readPosition() {
        if (!this.motion || !this.groupWidth) return;
        const distance = (Number(this.motion.currentTime) * this.speed / 1000) % this.groupWidth;
        this.position = this.direction === 'ltr' ? this.groupWidth - distance : distance;
        this.normalizePosition();
      }

      normalizePosition() {
        if (!this.groupWidth) return;
        while (this.position >= this.groupWidth) this.position -= this.groupWidth;
        while (this.position < 0) this.position += this.groupWidth;
      }

      applyTransform() {
        if (!this.motion) return;
        const distance = this.direction === 'ltr' ? this.groupWidth - this.position : this.position;
        this.motion.currentTime = distance / this.speed * 1000;
      }

      handlePointerDown(event) {
        if (event.button !== 0 || !event.isPrimary || !this.groupWidth) return;

        this.readPosition();
        this.suppressClick = false;
        this.activePointerId = event.pointerId;
        this.dragging = true;
        this.updatePlaybackRate();
        if (event.pointerType !== 'mouse') this.classList.add('is-touch-active');
        this.dragMoved = false;
        this.dragStartX = event.clientX;
        this.dragStartPosition = this.position;
      }

      handlePointerMove(event) {
        if (!this.dragging || event.pointerId !== this.activePointerId) return;

        const movement = event.clientX - this.dragStartX;
        if (!this.dragMoved) {
          if (Math.abs(movement) <= 6) return;
          this.dragMoved = true;
          // Capture only a real drag so ordinary clicks keep their anchor target.
          this.viewport.setPointerCapture(event.pointerId);
          this.classList.add('is-dragging');
        }
        this.position = this.dragStartPosition - movement;
        this.normalizePosition();
        this.applyTransform();
      }

      handlePointerUp(event) {
        if (!this.dragging || event.pointerId !== this.activePointerId) return;

        if (this.viewport.hasPointerCapture(event.pointerId)) {
          this.viewport.releasePointerCapture(event.pointerId);
        }
        this.dragging = false;
        this.classList.remove('is-dragging');
        this.updatePlaybackRate();

        this.suppressClick = this.dragMoved && event.type !== 'pointercancel';
      }

      handleClick(event) {
        if (!this.suppressClick || event.detail === 0) return;
        this.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }
    }
  );
}
